// Broker Deals data-quality rules and derived metrics for the legacy
// UPPER_SNAKE row shape. Imported by trsAggregation.js and
// dealMap/DealMap.jsx. Pure functions — no React, no API, no side effects.
//
// Status scopes are NOT defined here any more: they live in
// src/utils/dealStatusVocab.js, keyed to the canonical 10-value vocabulary
// the database stores. This file used to hold its own copy of the retired
// 21-value legacy vocabulary ("Closed", "Rejected", "Minerals Sold", …),
// which matched nothing — so `isOpen` returned true for every dead deal.
// The scopes are re-exported below so existing importers keep working.

import {
  CLOSED_STATUS,
  COMMITTED_OR_CLOSED_STATUSES,
  DEAD_STATUSES,
  DEFAULT_STALE_DAYS,
  PIPELINE_STATUSES,
  STALE_THRESHOLDS_DAYS,
  TERMINAL_STATUSES,
  dealStatusOf,
  isDeadStatus,
  isOpenStatus,
  normalizeStatus,
  staleThresholdFor,
} from "./dealStatusVocab";
import { parseDateMs } from "./dateFormat";

export {
  COMMITTED_OR_CLOSED_STATUSES,
  DEAD_STATUSES,
  DEFAULT_STALE_DAYS,
  PIPELINE_STATUSES,
  STALE_THRESHOLDS_DAYS,
  TERMINAL_STATUSES,
  staleThresholdFor,
};

// Closed-but-not-yet-fully-recorded, and fully recorded. NOTE: these read the
// legacy MSSQL recording columns (RECORDING_DATE / DATE_PAID_TO_SELLER /
// EXECUTED_ASSIGNMENT), which the normalized Postgres `deals` row does not
// carry — recording / payment now live on the lease_deeds, payments and
// assignment_recordings child tables. Against new-shape rows isFullyRecorded
// is therefore always false. Left in place for the legacy-shaped callers;
// see the note on `isOpen` below.
export const isClosing = (row) =>
  normalizeStatus(dealStatusOf(row)) === CLOSED_STATUS &&
  (!row?.RECORDING_DATE ||
    !row?.DATE_PAID_TO_SELLER ||
    !row?.EXECUTED_ASSIGNMENT);

export const isFullyRecorded = (row) =>
  normalizeStatus(dealStatusOf(row)) === CLOSED_STATUS &&
  !!row?.RECORDING_DATE &&
  !!row?.DATE_PAID_TO_SELLER &&
  !!row?.EXECUTED_ASSIGNMENT;

// An "open" deal: still live work — not dead and not CLOSED. (It used to
// count CLOSED-but-unrecorded deals as open, but recording state is no longer
// on the deal row, so that test could never be satisfied; scoping "open" to
// the status alone is the honest reading.)
export const isOpen = (row) => isOpenStatus(dealStatusOf(row));

export const isDead = (row) => isDeadStatus(dealStatusOf(row));

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

const MS_PER_DAY = 1000 * 60 * 60 * 24;

// Every consumer below compares against a local-midnight `today`, so
// date-only values must parse as local midnight — see `parseDateMs`.
const parseTs = parseDateMs;

const numOrNull = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// Days since an ISO date (floor). Returns null if unparseable.
export const daysSince = (iso) => {
  const t = parseTs(iso);
  if (t == null) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.floor((today.getTime() - t) / MS_PER_DAY);
};

// Days until an ISO date (floor; negative if in the past). Returns null if
// unparseable.
export const daysUntil = (iso) => {
  const t = parseTs(iso);
  if (t == null) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.floor((t - today.getTime()) / MS_PER_DAY);
};

export const titleCase = (snake) =>
  String(snake || "")
    .toLowerCase()
    .split("_")
    .map((w) => (w.length ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");

// ---------------------------------------------------------------------------
// Data-quality validators. Each test() returns true if the row FAILS the
// rule (i.e. is "bad"). Rows that fail any rule are excluded from numeric
// aggregates and surfaced in the DataQualityBadges strip.
// ---------------------------------------------------------------------------

export const DATA_QUALITY_RULES = [
  {
    key: "netGtGross",
    label: "Net Acres > Gross Acres",
    test: (r) =>
      r?.NET_ACRES != null &&
      r?.GROSS_ACRES != null &&
      Number(r.NET_ACRES) > Number(r.GROSS_ACRES),
  },
  {
    // Delivered NRI is a decimal fraction of 8/8ths (0–1), so a valid value
    // never exceeds 1. Anything above 1 is bad data — typically a percent-scale
    // value (e.g. 80) that leaked into a decimal column (UAT #30).
    key: "nriOver1",
    label: "Delivered NRI > 1",
    test: (r) => r?.DELIVERED_NRI != null && Number(r.DELIVERED_NRI) > 1,
  },
  {
    key: "effAfterExp",
    label: "Effective Date > Expiration",
    test: (r) =>
      !!r?.EFFECTIVE_DATE &&
      !!r?.EXPIRATION_DATE &&
      String(r.EFFECTIVE_DATE) > String(r.EXPIRATION_DATE),
  },
  {
    key: "closedNotPaid",
    label: "Closed without payment",
    test: (r) =>
      normalizeStatus(dealStatusOf(r)) === CLOSED_STATUS &&
      !r?.DATE_PAID_TO_SELLER,
  },
  {
    key: "bpaNoAcres",
    label: "BPA present but Net Acres missing",
    test: (r) =>
      r?.BONUS_PER_ACRE != null &&
      (r?.NET_ACRES == null || Number(r.NET_ACRES) === 0),
  },
  {
    key: "bonusMath",
    label: "Total Bonus ≠ BPA × Net Acres (±1%)",
    test: (r) => {
      if (r?.TOTAL_BONUS == null || r?.BONUS_PER_ACRE == null || r?.NET_ACRES == null)
        return false;
      const tb = Number(r.TOTAL_BONUS);
      const bpa = Number(r.BONUS_PER_ACRE);
      const net = Number(r.NET_ACRES);
      if (!Number.isFinite(tb) || !Number.isFinite(bpa) || !Number.isFinite(net))
        return false;
      const computed = bpa * net;
      if (computed === 0) return false;
      return Math.abs(tb - computed) / Math.abs(computed) > 0.01;
    },
  },
];

// Single-pass partition. Returns { cleanRows, badRows, badRowsByRule }.
// badRowsByRule is a Map<ruleKey, Set<dealKey>>.
export const partitionDataQuality = (rows) => {
  const safe = Array.isArray(rows) ? rows : [];
  const cleanRows = [];
  const badRows = [];
  const badRowsByRule = new Map();
  for (const rule of DATA_QUALITY_RULES) badRowsByRule.set(rule.key, new Set());

  for (const r of safe) {
    let failed = false;
    for (const rule of DATA_QUALITY_RULES) {
      let bad = false;
      try {
        bad = !!rule.test(r);
      } catch {
        bad = false;
      }
      if (bad) {
        failed = true;
        const key = r?.DEAL_KEY ?? r?.DEAL_ID ?? r;
        badRowsByRule.get(rule.key).add(key);
      }
    }
    if (failed) badRows.push(r);
    else cleanRows.push(r);
  }

  return { cleanRows, badRows, badRowsByRule };
};

// Convenience for renderers that just need "does this row fail any rule".
export const rowFailsAnyDqRule = (row) => {
  for (const rule of DATA_QUALITY_RULES) {
    try {
      if (rule.test(row)) return true;
    } catch {
      // noop
    }
  }
  return false;
};

// ---------------------------------------------------------------------------
// OOP past-due classification. Key off WRITTEN_ACCEPTANCE_RECEIVED + OOP_DAYS
// (NOT the PAYMENT_DUE_DATE column) per landman + analyst advisors. Returns:
//   null                   — not past due (or unknowable)
//   "weg_unfunded"         — internal problem: WEG hasn't wired the broker
//   "broker_not_disbursed" — broker problem: has WEG funds, hasn't paid seller
// ---------------------------------------------------------------------------
export const oopPastDue = (row, today = new Date()) => {
  if (!row?.WRITTEN_ACCEPTANCE_RECEIVED || !row?.OOP_DAYS) return null;
  if (row?.DATE_PAID_TO_SELLER) return null;
  const acceptTs = parseTs(row.WRITTEN_ACCEPTANCE_RECEIVED);
  if (acceptTs == null) return null;
  const oopDays = Number(row.OOP_DAYS);
  if (!Number.isFinite(oopDays)) return null;
  const dueBy = acceptTs + oopDays * MS_PER_DAY;
  const todayMs =
    today instanceof Date ? today.getTime() : Date.parse(today) || Date.now();
  if (dueBy >= todayMs) return null;
  if (!row?.DATE_PMT_RECD_FROM_WEG) return "weg_unfunded";
  return "broker_not_disbursed";
};

// Is a row "stale" for its current status, per landman's per-status thresholds?
// Falls back to STATUS_CHG_DATE ?? IMPORT_DATE; falls back to a 30-day default
// when the status has no threshold mapping.
export const isStale = (row) => {
  const iso = row?.STATUS_CHG_DATE ?? row?.IMPORT_DATE;
  const d = daysSince(iso);
  if (d == null) return false;
  const threshold = staleThresholdFor(dealStatusOf(row));
  return d > threshold;
};

// ---------------------------------------------------------------------------
// Aggregates. All aggregates operate on already-clean rows (caller is
// responsible for DQ-filtering beforehand via partitionDataQuality).
// ---------------------------------------------------------------------------

// Weighted average bonus-per-acre (weights = NET_ACRES). Excludes DEAD
// statuses; Closed is kept since it represents market-clearing pricing.
// Drops rows with NULL BPA or NULL net acres.
export const WEIGHTED_AVG_BPA = (rows) => {
  const safe = Array.isArray(rows) ? rows : [];
  let num = 0;
  let den = 0;
  for (const r of safe) {
    if (isDeadStatus(dealStatusOf(r))) continue;
    const bpa = numOrNull(r?.BONUS_PER_ACRE);
    const net = numOrNull(r?.NET_ACRES);
    if (bpa == null || net == null) continue;
    num += bpa * net;
    den += net;
  }
  return den > 0 ? num / den : 0;
};

// Sum of TOTAL_BONUS scoped to COMMITTED_OR_CLOSED_STATUSES.
export const TOTAL_BONUS_COMMITTED = (rows) => {
  const safe = Array.isArray(rows) ? rows : [];
  let sum = 0;
  for (const r of safe) {
    if (!COMMITTED_OR_CLOSED_STATUSES.has(dealStatusOf(r))) continue;
    const tb = numOrNull(r?.TOTAL_BONUS);
    if (tb != null) sum += tb;
  }
  return sum;
};

// Convenience: sum TOTAL_BONUS scoped to the pipeline statuses.
export const TOTAL_BONUS_PIPELINE = (rows) => {
  const safe = Array.isArray(rows) ? rows : [];
  let sum = 0;
  for (const r of safe) {
    if (!PIPELINE_STATUSES.has(dealStatusOf(r))) continue;
    const tb = numOrNull(r?.TOTAL_BONUS);
    if (tb != null) sum += tb;
  }
  return sum;
};

// ---------------------------------------------------------------------------
// Structural anomalies — surfaced in the DQ badges strip. Advisors
// (landman + lease-analyst) flagged these two as high-signal: duplicate
// DEAL_KEYs indicate a re-import collision, and orphans (open deals with
// no STATUS_CHG_DATE AND no IMPORT_DATE) have no timeline and can silently
// skip all stale-detection logic.
// ---------------------------------------------------------------------------

// Returns a Set<string> of DEAL_KEY values that appear more than once.
export const findDuplicateDealKeys = (rows) => {
  const safe = Array.isArray(rows) ? rows : [];
  const counts = new Map();
  for (const r of safe) {
    const key = r?.DEAL_KEY;
    if (key == null || key === "") continue;
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  const dupes = new Set();
  for (const [k, c] of counts.entries()) if (c > 1) dupes.add(k);
  return dupes;
};

// Returns the subset of open deals that have neither STATUS_CHG_DATE nor
// IMPORT_DATE — i.e. no timeline at all.
export const findOrphanDeals = (rows) => {
  const safe = Array.isArray(rows) ? rows : [];
  return safe.filter(
    (r) =>
      isOpen(r) &&
      (r?.STATUS_CHG_DATE == null || r?.STATUS_CHG_DATE === "") &&
      (r?.IMPORT_DATE == null || r?.IMPORT_DATE === "")
  );
};
